import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TControllerValue, TFilterSchemeOptions, TOptions } from '../../models';
import { FILTER_CONTROLLER_EVENTS, useSyncFilterValue } from '../../useSyncValue';

import { FiltersSwipeDialogOptionListItem } from './FiltersSwipeDialogOptionListItem';
import { softIncludes } from './utils';

import { AnyType } from '@src/models/common';
import { Box } from '@src/atoms/Box';
import { Input } from '@src/atoms/Input';

import { ISwipeDialogComponentProps } from '../models';
import { FilterSwipeDialogWrapper } from '../_Wrapper/FilterSwipeDialogWrapper';

import styles from './FiltersSwipeDialogOptionList.scss';
import { isNotEmptyArray } from '@src/utils/array';

/** Компонент списка для опций */
export const FiltersSwipeDialogOptionList: FC<ISwipeDialogComponentProps> = (props) => {
  const { filter, setActiveFilterName, controller, loading } = props;

  const filterValues = useSyncFilterValue({ controller, event: filter.name }) as TControllerValue<AnyType[]>;
  const scheme = useSyncFilterValue({ controller, event: FILTER_CONTROLLER_EVENTS.SCHEME });

  /**
   * Актуальное значение фильтра надо получать именно так, чтобы видеть актуальный список опций и т.д.,
   * Родители выше не следят за этим
   */
  const { name, options, filterField = 'name', title } = scheme.get(filter.name) as TFilterSchemeOptions;

  const [values, setValues] = useState(filterValues?.value || null);
  const [isScroll, setIsScroll] = useState(false);
  const [search, setSearch] = useState('');

  /** Это список с изначально установленными значениями */
  const { defaultSelectedValuesMap } = useMemo(() => {
    const defaultSelectedValuesMap = new Map();

    if (filterValues?.value && Array.isArray(filterValues?.value) && filterValues?.value.length > 0) {
      filterValues?.value.forEach((v) => {
        defaultSelectedValuesMap.set(v.id, v);
      });
    }
    return { defaultSelectedValuesMap };
  }, [filterValues?.value]);

  const currentOptions = useMemo<TOptions>(
    () =>
      (options || [])
        .reduce(
          (acc, item, index) => {
            //Здесь уже добавленные опции нужно поместить в начало списка, для этого создаем два массива
            //Дефолтные опции добавляем в первый массив (т.е. это те которые уже добавлены)
            //Остальные во второй.
            defaultSelectedValuesMap.has(item.id) ? acc[0].push(item) : acc[1].push(item);

            //Если это последняя опция => объеденяем массивы в один
            return index === (options || []).length - 1 ? acc.flat() : acc;
          },
          [
            //Список с изначально установленными значениями
            [],
            //Остальной список
            [],
          ] as AnyType[],
        )
        .filter((o) => softIncludes(o[filterField], search)),
    [filterField, options, search, defaultSelectedValuesMap],
  );

  /** Здесь храним историю измененний относительно дефолтного состояния */
  const changedSet = useRef(new Set());

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleClose = useCallback(() => {
    setActiveFilterName(null);
  }, [setActiveFilterName]);

  const handleClick = () => {
    controller.onChange(name, values);
    setActiveFilterName(null);
  };

  const handleReset = () => {
    setValues(filterValues?.value || null);
    changedSet.current.clear();
  };

  const handleChange = useCallback(
    (option: AnyType, method: 'remove' | 'add') => {
      let newValue: AnyType[] | null = null;

      changedSet.current.has(option.id) ? changedSet.current.delete(option.id) : changedSet.current.add(option.id);

      if (method === 'add') {
        newValue = [...(values || []), option];
      } else if (method === 'remove') {
        newValue = values?.filter((o) => o.id !== option.id) || null;
      }

      setValues(newValue);
    },
    [values],
  );

  useEffect(() => {
    controller.loadOption(filter as AnyType);
  }, [controller, filter, values]);

  return (
    <FilterSwipeDialogWrapper
      isHasInput
      isScroll={isScroll}
      loading={loading}
      resultText="Применить"
      title={title}
      isDisabledSubmitBtn={changedSet.current.size === 0}
      rightHeaderBtn={{
        text: 'Сбросить',
        onClick: handleReset,
      }}
      onSubmit={handleClick}
      onBack={handleClose}
    >
      <>
        {isScroll && (
          <Box padding={16}>
            <Input
              id="SearchFilterPropertyOptionInput"
              preIcon="search"
              showResetButton
              value={search}
              onChange={handleSearch}
            />
          </Box>
        )}

        <Box
          column
          className={styles.container}
          ref={(element) => {
            /**
             * По факту нам надо попробовать определить только при первом рендере
             * (т.е. когда отображается полный список) есть ли скролл, далее уже не важно
             **/
            const value = element ? element.scrollHeight > element.clientHeight : false;
            setIsScroll((prev) => (!prev ? value : prev));
          }}
        >
          {isNotEmptyArray(currentOptions) ? (
            currentOptions.map((option, i, arr) => (
              <FiltersSwipeDialogOptionListItem
                key={option.id}
                filter={filter}
                option={option}
                value={Boolean(values?.some((v) => v.id === option.id))}
                onClick={handleChange}
                isLast={arr.length - 1 === i}
              />
            ))
          ) : (
            <Box
              paddingTop={32}
              paddingBottom={32}
              paddingLeft={48}
              paddingRight={48}
              alignItems="center"
              justifyContent="center"
            >
              Нет доступных значений фильтров
            </Box>
          )}
        </Box>
      </>
    </FilterSwipeDialogWrapper>
  );
};
